import { Injectable, inject } from "@angular/core";
import { Router, RouterStateSnapshot } from "@angular/router";
import { Auth, User, authState } from "@angular/fire/auth";
import { Observable, firstValueFrom, of } from "rxjs";
import { catchError, filter } from "rxjs/operators";
import { SessionUserDataService } from "@shared/services/user-data/session-user-data.service";
import { requiresPhoneNumber } from "@shared/helpers/phone-requirement.helper";

@Injectable({
  providedIn: "root",
})
export class RouteAccessService {
  private readonly auth: Auth = inject(Auth);
  private readonly router: Router = inject(Router);
  private readonly userDataService: SessionUserDataService = inject(SessionUserDataService);

  /** Extensión point para tests — permite inyectar un auth state observable controlado. */
  protected getAuthState(): Observable<User | null> {
    return authState(this.auth);
  }

  constructor() {
    this.getAuthState()
      .pipe(filter((user: User | null): boolean => user === null && this.userDataService.hasStoredSession()))
      .subscribe((): void => {
        this.userDataService.clear();
        void this.router.navigate(["/login"]);
      });
  }

  async isAuthenticated(): Promise<boolean> {
    if (this.userDataService.hasStoredSession()) return true;

    return (await firstValueFrom(this.getAuthState())) !== null;
  }

  async canActivateRoute(state: RouterStateSnapshot): Promise<boolean> {
    if (!(await this.isAuthenticated())) {
      this.router.navigate(["/login"]);
      return false;
    }

    await this.loadProfile();

    if (!this.validatePhoneNumber(state)) return false;

    return true;
  }

  private async loadProfile(): Promise<void> {
    await firstValueFrom(this.userDataService.loadUserData$().pipe(catchError(() => of(null))));
  }

  private validatePhoneNumber(state: RouterStateSnapshot): boolean {
    const immuneRoutes: string[] = ["/main"];
    const isImmuneRoute: boolean = immuneRoutes.some((route: string) =>
      state.url.includes(route)
    );
    const phoneNumber: string | null | undefined = this.userDataService.user()?.phoneNumber;

    if (!isImmuneRoute && !phoneNumber && requiresPhoneNumber(this.userDataService.user())) {
      this.router.navigate(["/main"]);
      return false;
    }

    return true;
  }
}
